import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  defer as observableDefer,
  Observable,
  of as observableOf,
  throwError as observableThrowError,
  catchError,
  map,
  timeout,
} from 'rxjs';
import { TimeoutLimit } from '../../models/timeout-limit';
import { timeoutError } from '../../models/timeout-error';
import { CertificateTransactionInfoResponse } from '../../dtos/response/certificate-transaction-info-response/certificate-transaction-info-response';
import { CertificateTransactionInfo } from '../../dtos/response/certificate-transaction-info-response/certificate-transaction-info';
import { GenericResponseStatus } from '../../models/generic-response-status';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';

@Injectable({
  providedIn: 'root',
})
export class MPFlightTransactionService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly eventService: GlobalEventService = inject(GlobalEventService);
  private readonly certificateTransactionInfoEndpoint = 'api/certificates/transaction-info';

  constructor() {}

  public getCertificateTransactionInfo(certificateNumbers?: string[]): Observable<CertificateTransactionInfoResponse> {
    let params = new HttpParams();
    certificateNumbers.forEach((number) => {
      params = params.append('certificateNumbers', number);
    });

    const options = {
      headers: new HttpHeaders({
        background: 'true',
      }),
      params,
    };

    return this.httpClient.get<CertificateTransactionInfo[]>(this.certificateTransactionInfoEndpoint, options).pipe(
      timeout({
        each: TimeoutLimit.SHORT,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => {
        if (response.every((transactionInfo) => transactionInfo.errorCode === 'Successful')) {
          // All certificate numbers could retieve transaction info
          return { status: GenericResponseStatus.SUCCESS, response };
        } else if (response.some((transactionInfo) => transactionInfo.errorCode === 'Successful')) {
          // Partial success, some certificate numbers could not retrieve transaction info
          return { status: GenericResponseStatus.PARTIAL_FAILURE, response };
        } else {
          // Failure, no certificate numbers could retrieve transaction info
          return { status: GenericResponseStatus.FAILURE, response };
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle error case
        if (timeoutError.status === error.status) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, error);
          return observableOf({ status: GenericResponseStatus.FAILURE, response: [], exceptionContent: 'Timeout' });
        } else {
          // Uncaught error, use the error object as a string
          return observableOf({
            status: GenericResponseStatus.FAILURE,
            response: [],
            exceptionContent: error.error ? JSON.stringify(error.error) : 'Uncaught',
          });
        }
      })
    );
  }
}
